import Skeleton from "./Skeleton";

/**
 * Full-page placeholder for the mobile article page while the detail query
 * resolves without a feed-seeded cache (direct URL entry, hard reload).
 * Mirrors ArticlePageHeader + ArticlePageHero + tabs + body layout.
 */
export function ArticlePageSkeleton() {
  return (
    <div
      className="flex size-full flex-col bg-white"
      // Same device-inset handling as ScrollableLayout's header slot — without
      // it the fake header sits under the notch/status bar in the native
      // webview.
      style={{ paddingTop: "var(--safe-area-top)" }}
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3">
        <Skeleton $variant="circular" width={32} height={32} />
        <div className="flex gap-3">
          <Skeleton $variant="circular" width={32} height={32} />
          <Skeleton $variant="circular" width={32} height={32} />
        </div>
      </div>

      {/* Hero: date + access badge, category, title, journal/author rows */}
      <div className="flex flex-col gap-2 px-5 py-3">
        <div className="flex items-center justify-between">
          <Skeleton $variant="rectangular" width={88} height={12} />
          <Skeleton $variant="pill" width={80} height={20} />
        </div>
        <Skeleton $variant="rectangular" width={112} height={12} />
        <div className="mt-1 flex flex-col gap-1.5">
          <Skeleton $variant="rectangular" width="100%" height={20} />
          <Skeleton $variant="rectangular" width="92%" height={20} />
          <Skeleton $variant="rectangular" width="61%" height={20} />
        </div>
        <div className="mt-1 flex flex-col gap-2">
          <Skeleton $variant="rectangular" width="45%" height={12} />
          <Skeleton $variant="rectangular" width="35%" height={12} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 px-5 py-2">
        <Skeleton $variant="pill" width={88} height={32} />
        <Skeleton $variant="pill" width={88} height={32} />
        <Skeleton $variant="pill" width={88} height={32} />
      </div>

      {/* Body: TLDR lines + figure block */}
      <div className="flex flex-col gap-2 px-5 py-4">
        <Skeleton $variant="rectangular" width="100%" height={14} />
        <Skeleton $variant="rectangular" width="100%" height={14} />
        <Skeleton $variant="rectangular" width="96%" height={14} />
        <Skeleton $variant="rectangular" width="88%" height={14} />
        <Skeleton $variant="rectangular" width="52%" height={14} />
        <Skeleton
          $variant="rounded"
          width="100%"
          height={180}
          className="mt-3"
        />
      </div>
    </div>
  );
}

/**
 * Desktop counterpart: mirrors DesktopArticlePage, its top bar and its 720px
 * reading column (same width and padding classes as the real page): the
 * journal line, the title, the authors, the meta row, then the reading.
 */
export function DesktopArticlePageSkeleton() {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Top bar (DesktopTopNav): breadcrumb, then the search trigger */}
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-ds-slate-100 px-6">
        <Skeleton $variant="rectangular" width={140} height={16} />
        <Skeleton $variant="rectangular" width={280} height={36} />
      </div>

      <div className="flex-1 overflow-hidden px-8">
        <div className="mx-auto flex max-w-desktop-reading flex-col gap-3 pt-8">
          {/* Journal and DOI */}
          <Skeleton $variant="rectangular" width="45%" height={16} />

          {/* Title */}
          <div className="flex flex-col gap-2">
            <Skeleton $variant="rectangular" width="100%" height={32} />
            <Skeleton $variant="rectangular" width="88%" height={32} />
            <Skeleton $variant="rectangular" width="55%" height={32} />
          </div>

          {/* Authors */}
          <Skeleton $variant="rectangular" width="20%" height={13} />

          {/* Meta row and actions */}
          <div className="flex items-center justify-between">
            <Skeleton $variant="rectangular" width="36%" height={20} />
            <Skeleton $variant="rectangular" width={220} height={32} />
          </div>

          {/* Reading */}
          <div className="mt-10 flex flex-col gap-2.5">
            <Skeleton $variant="rectangular" width="100%" height={16} />
            <Skeleton $variant="rectangular" width="100%" height={16} />
            <Skeleton $variant="rectangular" width="97%" height={16} />
            <Skeleton $variant="rectangular" width="92%" height={16} />
            <Skeleton $variant="rectangular" width="60%" height={16} />
          </div>
        </div>
      </div>
    </div>
  );
}
